import type { ComponentProps, ComponentType } from "react"
import { useQuery } from "@tanstack/react-query"
import { NavLink, useLocation } from "react-router-dom"
import {
  BriefcaseBusiness,
  Building2,
  ChartNoAxesCombined,
  CircleDollarSign,
  CalendarCheck2,
  ListChecks,
  Settings,
  TableProperties,
  TrendingUp,
  Users,
  WalletCards,
} from "lucide-react"
import type { User } from "@pishgam/contracts"
import pishgamLogo from "@/assets/pishgam-ventures.jpg"
import { api } from "@/api"
import { fa, type Task, type TasksResponse } from "@/process"
import { cn } from "@/lib/utils"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/sidebar"

type NavItem = {
  to: string
  label: string
  icon: ComponentType<{ className?: string }>
}
type NavSection = { label: string; items: NavItem[] }

/** Navigation is the union of everything the person is (staff, head, CFO, board, admin). */
export function sectionsFor(user: User): NavSection[] {
  const caps = user.capabilities
  const sections: NavSection[] = [
    {
      label: "کارهای من",
      items: [
        { to: "/dashboard", label: caps.board && !caps.submitsAllocation ? "خانه" : "کارهای من", icon: ListChecks },
        ...(caps.submitsAllocation ? [{ to: "/allocations", label: "نسبت کاری من", icon: BriefcaseBusiness }] : []),
      ],
    },
  ]
  const approvals: NavItem[] = [
    ...(caps.approvesDivisions.length || caps.admin ? [{ to: "/approvals/divisions", label: "تأیید مدیر واحد", icon: Building2 }] : []),
    ...(caps.approvesUnits.length || caps.admin ? [{ to: "/approvals/business-units", label: "تأیید مدیر کسب‌وکار", icon: Users }] : []),
  ]
  if (approvals.length) sections.push({ label: "تأییدها", items: approvals })
  if (caps.finance) {
    sections.push({
      label: "مالی",
      items: [
        { to: "/payroll", label: "حقوق و سایر هزینه‌ها", icon: WalletCards },
        { to: "/finance/entry", label: "درآمدها", icon: TableProperties },
        { to: "/finance/close", label: "پایش بستن ماه", icon: CalendarCheck2 },
      ],
    })
  }
  if (caps.forecastUnits.length && !caps.finance) sections.push({ label: "برنامه‌ریزی", items: [{ to: "/forecast", label: "پیش‌بینی کسب‌وکار من", icon: TrendingUp }] })
  if (caps.forecastUnits.length && caps.finance) sections.find((section) => section.label === "مالی")!.items.push({ to: "/forecast", label: "پیش‌بینی کسب‌وکار من", icon: TrendingUp })
  if (caps.companyReports) {
    sections.push({
      label: "گزارش‌ها",
      items: [
        { to: "/analytics/pnl", label: "سود و زیان", icon: ChartNoAxesCombined },
        { to: "/planning/fpa", label: "برنامه‌ریزی مالی", icon: CircleDollarSign },
      ],
    })
  }
  if (caps.admin) sections.push({ label: "مدیریت", items: [{ to: "/admin", label: "کاربران و سازمان", icon: Settings }] })
  return sections
}

/** The nav entry (and its section) that owns a path: longest matching prefix wins. */
export function currentNav(user: User, pathname: string) {
  let best: { section: string; item: NavItem } | null = null
  for (const section of sectionsFor(user)) {
    for (const item of section.items) {
      const match = pathname === item.to || pathname.startsWith(`${item.to}/`)
      if (match && (!best || item.to.length > best.item.to.length)) best = { section: section.label, item }
    }
  }
  return best
}

// Which nav entry an open task belongs to, so the sidebar can show what is waiting where.
const TASK_ROUTE: Partial<Record<Task["kind"], string>> = {
  allocation: "/allocations", division: "/approvals/divisions", unit: "/approvals/business-units",
  payroll: "/payroll", costs: "/finance/entry", close: "/finance/close", forecast: "/forecast",
}

function useNavSignals() {
  const tasks = useQuery({ queryKey: ["process-tasks"], queryFn: () => api<TasksResponse>("/process/tasks") })
  const signals = new Map<string, { count?: number; overdue: boolean }>()
  for (const task of tasks.data?.tasks ?? []) {
    const route = TASK_ROUTE[task.kind]
    if (!route || task.done) continue
    signals.set(route, { count: task.kind === "close" ? undefined : task.count, overdue: task.overdue })
  }
  return signals
}

export function AppSidebar({ user, ...props }: ComponentProps<typeof Sidebar> & { user: User }) {
  const location = useLocation()
  const active = currentNav(user, location.pathname)?.item.to
  const signals = useNavSignals()

  return (
    <Sidebar {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <NavLink to="/dashboard">
                <img src={pishgamLogo} alt="" className="size-10 shrink-0 rounded-lg border bg-background object-cover" />
                <div className="grid min-w-0 flex-1 gap-0.5 text-right leading-tight">
                  <span className="text-sm font-semibold">سامانه سود و زیان</span>
                  <span className="text-xs leading-4 whitespace-normal text-muted-foreground">شرکت توسعه ابزارهای جسورانه پیشگام</span>
                </div>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {sectionsFor(user).map((section) => (
        <SidebarGroup key={section.label}>
          <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {section.items.map((item) => {
                const isActive = active === item.to
                const signal = signals.get(item.to)
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.label}
                      isActive={isActive}
                      className="relative text-start text-sidebar-foreground/80 data-active:bg-primary/10 data-active:font-semibold data-active:text-primary data-active:hover:bg-primary/15 data-active:hover:text-primary [&>span:last-child]:overflow-visible"
                    >
                      <NavLink to={item.to} aria-current={isActive ? "page" : undefined}>
                        {isActive && <span aria-hidden className="absolute inset-y-1.5 start-0 w-[3px] rounded-full bg-primary" />}
                        <item.icon className="size-4" />
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {signal && (
                          signal.count ? (
                            <span className={cn("ms-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums", signal.overdue ? "bg-loss text-white" : "bg-primary/12 text-primary")}>
                              {fa(signal.count)}<span className="sr-only">{signal.overdue ? " مورد، مهلت گذشته" : " مورد باز"}</span>
                            </span>
                          ) : (
                            <span className={cn("ms-auto size-2 rounded-full", signal.overdue ? "bg-loss" : "bg-primary")}><span className="sr-only">{signal.overdue ? "مهلت گذشته" : "کار باز"}</span></span>
                          )
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter />
      <SidebarRail />
    </Sidebar>
  )
}
